import { useState } from 'react';
import { Building2, Users, UserCog, UserCircle, History, Bot, Building, Mail, Bell, Settings as SettingsIcon, ChevronRight, ArrowLeft, CalendarRange } from 'lucide-react';
import AgencySettings from '../components/settings/AgencySettings';
import NegotiatorSettings from '../components/settings/NegotiatorSettings';
import UserSettings from '../components/settings/UserSettings';
import AssistantSettings from '../components/settings/AssistantSettings';
import HistorySettings from '../components/settings/HistorySettings';
import AISettings from '../components/settings/AISettings';
import CoproTabsSettings from '../components/settings/CoproTabsSettings';
import EmailSettings from '../components/settings/EmailSettings';
import NotificationPreferences from '../components/user/NotificationPreferences';
import ReunionMensuelleSettings from '../components/settings/reunion-mensuelle/ReunionMensuelleSettings';

type Tab = 'agencies' | 'negotiators' | 'assistants' | 'users' | 'history' | 'ai' | 'copros' | 'email' | 'notifications' | 'reunion';

interface CategoryItem {
  id: Tab;
  icon: any;
  label: string;
  description: string;
  color: string;
}

const ALL_ITEMS: CategoryItem[] = [
  { id: 'agencies', icon: Building2, label: 'Agences', description: 'Créer et gérer les agences du réseau', color: 'indigo' },
  { id: 'users', icon: UserCog, label: 'Utilisateurs', description: 'Gérer les comptes, rôles et permissions', color: 'violet' },
  { id: 'negotiators', icon: Users, label: 'Négociateurs', description: 'Objectifs et données des négociateurs', color: 'emerald' },
  { id: 'assistants', icon: UserCircle, label: 'Assistantes', description: 'Gérer les assistantes et leurs accès', color: 'rose' },
  { id: 'history', icon: History, label: 'Historique', description: "Consulter l'historique des actions", color: 'sky' },
  { id: 'ai', icon: Bot, label: 'IA', description: 'Configurer les fonctionnalités IA', color: 'fuchsia' },
  { id: 'copros', icon: Building, label: 'Copro', description: 'Gestion des copropriétés', color: 'teal' },
  { id: 'email', icon: Mail, label: 'Email', description: 'Configuration des emails', color: 'blue' },
  { id: 'notifications', icon: Bell, label: 'Notifications', description: 'Préférences de notifications', color: 'red' },
  { id: 'reunion', icon: CalendarRange, label: 'Réunion mensuelle', description: 'Délais de vente, ventes et honoraires', color: 'cyan' },
];

const COLOR_CLASSES: Record<string, string> = {
  indigo: 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600',
  violet: 'bg-violet-50 text-violet-600 group-hover:bg-violet-600',
  emerald: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600',
  rose: 'bg-rose-50 text-rose-600 group-hover:bg-rose-600',
  sky: 'bg-sky-50 text-sky-600 group-hover:bg-sky-600',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-600 group-hover:bg-fuchsia-600',
  teal: 'bg-teal-50 text-teal-600 group-hover:bg-teal-600',
  blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-600',
  red: 'bg-red-50 text-red-600 group-hover:bg-red-600',
  cyan: 'bg-cyan-50 text-cyan-600 group-hover:bg-cyan-600',
};

export default function Settings() {
  const [activeTab, setActiveTab] = useState<Tab | null>(null);

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-7 py-6 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
            <SettingsIcon size={22} className="text-indigo-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Paramètres</h1>
            <p className="text-sm text-slate-400 mt-0.5">Gestion des agences, négociateurs et configuration</p>
          </div>
        </div>
      </div>

      {activeTab === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ALL_ITEMS.map(({ id, icon: Icon, label, description, color }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="flex items-center gap-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-7 text-left hover:border-indigo-300 hover:shadow-md transition-all group"
            >
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 group-hover:text-white transition-colors ${COLOR_CLASSES[color]}`}>
                <Icon size={28} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-semibold text-slate-800">{label}</p>
                <p className="text-sm text-slate-500 mt-1">{description}</p>
              </div>
              <ChevronRight size={20} className="text-slate-300 shrink-0 group-hover:text-indigo-500 transition-colors" />
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* En-tête de la catégorie sélectionnée */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
            <button
              onClick={() => setActiveTab(null)}
              className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors"
            >
              <ArrowLeft size={16} />
              Paramètres
            </button>
            {(() => {
              const current = ALL_ITEMS.find(item => item.id === activeTab);
              if (!current) return null;
              const Icon = current.icon;
              return (
                <>
                  <span className="text-slate-300">/</span>
                  <Icon size={15} className="text-indigo-500" />
                  <span className="text-sm font-semibold text-slate-800">{current.label}</span>
                </>
              );
            })()}
          </div>

          {/* Content area */}
          <div className="p-6 overflow-auto">
          {activeTab === 'agencies'      && <AgencySettings />}
          {activeTab === 'negotiators'   && <NegotiatorSettings />}
          {activeTab === 'assistants'    && <AssistantSettings />}
          {activeTab === 'users'         && <UserSettings />}
          {activeTab === 'history'       && <HistorySettings />}
          {activeTab === 'ai'            && <AISettings />}
          {activeTab === 'copros'        && <CoproTabsSettings />}
          {activeTab === 'email'         && <EmailSettings />}
          {activeTab === 'notifications' && <NotificationPreferences />}
          {activeTab === 'reunion'       && <ReunionMensuelleSettings />}
          </div>
        </div>
      )}
    </div>
  );
}